import { useId, useState } from 'react';
import { useNavigate, useRevalidator } from 'react-router';

import { Badge, Button, Card, Icon } from '@ds/core';
import { DataTable } from '@ds/data';
import type { DataTableColumn } from '@ds/data';
import { Alert, Modal } from '@ds/feedback';
import { FormField, Input, SearchField, Switch } from '@ds/forms';
import { EtiquetaChip } from '@/comuns/EtiquetaChip';
import { ExportMenu } from '@/comuns/ExportMenu';
import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';
import type { CargaDeAtendimentoECrm, Etiqueta } from '@/data/cadastros';
import { SeletorDeCor } from './SeletorDeCor';

function normalizar(texto: string): string {
    return texto.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/**
 * Etiquetas — o cadastro simples: nome, cor e se está em uso.
 *
 * São as MESMAS etiquetas de Atendimentos & CRM. Aqui só se mexe no que o
 * protótipo mostrava; departamento, fila e gatilho ficam intactos e se editam
 * lá — duas telas para o mesmo campo discordariam.
 */
export function EtiquetasAba({ carga }: { carga: CargaDeAtendimentoECrm }) {
    const revalidator = useRevalidator();
    const navigate = useNavigate();
    const aviso = useAviso();
    const idNome = useId();

    const [busca, setBusca] = useState('');
    const [alvo, setAlvo] = useState<Etiqueta | 'nova' | null>(null);
    const [nome, setNome] = useState('');
    const [cor, setCor] = useState('#2986FF');
    const [ativa, setAtiva] = useState(true);
    const [salvando, setSalvando] = useState(false);

    const abrir = (e: Etiqueta | 'nova') => {
        setAlvo(e);
        setNome(e === 'nova' ? '' : e.nome);
        setCor(e === 'nova' ? '#2986FF' : e.cor);
        setAtiva(e === 'nova' ? true : e.ativa);
    };

    const sujo = alvo === 'nova'
        ? nome.trim() !== ''
        : alvo !== null && (nome !== alvo.nome || cor !== alvo.cor || ativa !== alvo.ativa);

    const salvar = async () => {
        if (!alvo) return;
        setSalvando(true);
        try {
            const existente = alvo === 'nova' ? undefined : alvo;
            await dados().salvarEtiqueta(existente?.id ?? null, {
                nome, cor, ativa,
                gatilhos: existente?.gatilhos ?? [],
                ...(existente?.departamentoId ? { departamentoId: existente.departamentoId } : {}),
                ...(existente?.filaId ? { filaId: existente.filaId } : {}),
            });
            aviso({ title: existente ? 'Etiqueta atualizada' : 'Etiqueta criada', description: nome.trim() });
            setAlvo(null);
            await revalidator.revalidate();
        } catch (e) {
            aviso({ title: 'Não foi possível salvar a etiqueta', description: e instanceof Error ? e.message : 'Tente de novo.', tone: 'danger' });
        } finally {
            setSalvando(false);
        }
    };

    const visiveis = carga.etiquetas.filter((e) => normalizar(e.nome).includes(normalizar(busca.trim())));

    const colunas: DataTableColumn<Etiqueta>[] = [
        { key: 'nome', header: 'Etiqueta', render: (e) => <EtiquetaChip nome={e.nome} cor={e.cor} /> },
        {
            key: 'gatilhos', header: 'Aplicação', width: 180,
            render: (e) => (e.gatilhos.length ? `automática · ${e.gatilhos.length} gatilho${e.gatilhos.length > 1 ? 's' : ''}` : 'manual'),
        },
        { key: 'ativa', header: 'Situação', width: 120, render: (e) => <Badge tone={e.ativa ? 'success' : 'neutral'}>{e.ativa ? 'Ativa' : 'Desativada'}</Badge> },
        { key: 'acoes', header: '', width: 100, align: 'right', render: (e) => <Button size="sm" variant="secondary" onClick={() => abrir(e)}>Editar</Button> },
    ];

    return (
        <>
            <Card
                title="Etiquetas" padding={0}
                subtitle="As mesmas de Atendimentos & CRM, onde se escolhe departamento, fila e gatilho."
                actions={(
                    <span style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <Button size="sm" variant="ghost" onClick={() => { void navigate('/app/configuracoes/atendimento-crm'); }}>Departamento e gatilhos</Button>
                        <ExportMenu
                            payload={{
                                title: 'Etiquetas',
                                columns: [
                                    { header: 'Etiqueta', value: (e: Etiqueta) => e.nome },
                                    { header: 'Cor', value: (e: Etiqueta) => e.cor },
                                    { header: 'Gatilhos', value: (e: Etiqueta) => e.gatilhos.join(', ') },
                                    { header: 'Situação', value: (e: Etiqueta) => (e.ativa ? 'Ativa' : 'Desativada') },
                                ],
                                rows: visiveis,
                            }}
                        />
                        <Button size="sm" iconLeft={<Icon name="plus" size={14} />} onClick={() => abrir('nova')}>Adicionar etiqueta</Button>
                    </span>
                )}
            >
                <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-subtle)' }}>
                    <SearchField value={busca} onChange={setBusca} placeholder="Buscar etiqueta" ariaLabel="Buscar etiqueta" />
                </div>
                <DataTable
                    columns={colunas} rows={visiveis} rowKey="id" stickyHeader={false} style={{ border: 0, borderRadius: 0 }}
                    emptyMessage={busca ? `Nenhuma etiqueta com “${busca}”.` : 'Nenhuma etiqueta ainda.'}
                />
            </Card>

            <Modal
                open={alvo !== null} width={480}
                title={alvo === 'nova' ? 'Nova etiqueta' : 'Editar etiqueta'}
                onClose={() => setAlvo(null)} dirty={sujo} onSave={() => { void salvar(); }}
                footer={(
                    <>
                        <Button variant="ghost" onClick={() => setAlvo(null)}>Cancelar</Button>
                        <Button onClick={() => { void salvar(); }} disabled={!nome.trim() || salvando}>{salvando ? 'Salvando…' : 'Salvar'}</Button>
                    </>
                )}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <FormField label="Nome" htmlFor={idNome} required>
                        <Input id={idNome} value={nome} onChange={setNome} placeholder="Primeira consulta" />
                    </FormField>
                    <FormField label="Cor" required hint="A paleta evita tons de vinho de propósito: etiqueta em bordô se confunde com o acento da marca.">
                        <SeletorDeCor valor={cor} onChange={setCor} rotulo="Cor da etiqueta" />
                    </FormField>
                    <span><EtiquetaChip nome={nome.trim() || 'Prévia da etiqueta'} cor={cor} /></span>
                    {alvo !== 'nova' && alvo?.gatilhos.length ? (
                        <Alert tone="neutral" title="Esta etiqueta também se aplica sozinha">
                            Os gatilhos ({alvo.gatilhos.join(', ')}) continuam valendo; mudam em Atendimentos & CRM.
                        </Alert>
                    ) : null}
                    <Switch checked={ativa} onChange={setAtiva} label="Ativa" description="Desativada, não é aplicada em conversa nova e continua nas que já a têm." />
                </div>
            </Modal>
        </>
    );
}
